import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';
import it from './locales/it.json';

// UI copy (02 §2.3: every string through t()). Italian is the primary
// language, English second. The bundles are compiled in (small, and the
// console works offline behind Caddy); the language follows the user's
// `locale` once signed in, else the stored pick, else the browser.

export const LANGUAGES = ['it', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];
export const LANGUAGE_STORAGE_KEY = 'plancia.lang';

function initialLanguage(): Language {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'it' || stored === 'en') return stored;
  } catch {
    /* ignore */
  }
  if (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('en')) return 'en';
  return 'it';
}

// Each screen area keeps its own namespace in locales/<lang>/<area>.json
// (`useTranslation('<area>')`), so parallel work never edits one shared file.
const areaFiles = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/*.json', {
  eager: true,
});
const resources: Record<string, Record<string, Record<string, unknown>>> = {
  it: { translation: it },
  en: { translation: en },
};
for (const [path, mod] of Object.entries(areaFiles)) {
  const match = /\.\/locales\/(\w+)\/([\w-]+)\.json$/.exec(path);
  if (!match) continue;
  const [, lang, area] = match as unknown as [string, string, string];
  resources[lang] = { ...resources[lang], [area]: mod.default };
}

void i18n.use(initReactI18next).init({
  resources,
  ns: ['translation', ...new Set(Object.values(resources).flatMap((r) => Object.keys(r)))],
  defaultNS: 'translation',
  lng: initialLanguage(),
  fallbackLng: 'it',
  interpolation: { escapeValue: false },
  returnNull: false,
});

export function setLanguage(language: Language): void {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(language);
  document.documentElement.lang = language;
}

/**
 * Signed in: the person's `locale` (S25 «Lingua») wins over the browser,
 * unless they picked a language in this browser (user menu, ⌘K).
 */
export function followUserLocale(locale: Language): void {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === 'it' || stored === 'en') return;
  } catch {
    /* ignore */
  }
  if (i18n.language === locale) return;
  void i18n.changeLanguage(locale);
  document.documentElement.lang = locale;
}

export default i18n;
